/**
 * Customer-facing copy for TRX PayPage hosted-field failures (§11.9 taxonomy),
 * and the typed errors the extract paths throw.
 *
 * WHY THIS IS A MODULE AND NOT TWO PRIVATE FUNCTIONS
 * --------------------------------------------------
 * `TrxCardFields` and `TrxAchFields` each carried their own private
 * `describeTrxError`, and each threw a BARE `Error`. That second detail is the
 * one that cost a real charge:
 *
 *   True Wealth invoice 1042, 2026-09-03. An operator keyed an invalid card
 *   number. TRX's fields refused to tokenize and said exactly why
 *   (`{ type: 'wrong-field', field: 'card-number' }`). A tokenize is NOT a
 *   sale, so TRX's portal showed no decline; our API was never called, so no
 *   server log existed; and the extract throw sits outside the only
 *   `logger.error` on the charge path, so no client log existed either. The
 *   thrown message was the single carrier of the reason — and because the error
 *   had no `code`, `resolveCandidateCode` returned undefined and
 *   `translateError` fell through to `GENERIC_FALLBACK`, which DELIBERATELY
 *   discards `error.message` (`translateError.ts:334`). The operator was shown
 *   "Something went wrong. Please try again." for a typo.
 *
 * So the copy and the CODE have to travel together, which is what these
 * builders guarantee. It is the same defect class the catalog already records
 * at `errorCatalog.ts:1657` for the charge-outcome codes, recurring one layer
 * down at the hosted-fields boundary.
 *
 * A hosted-field refusal is a VALIDATION REFUSAL in the catalog's sense: the
 * specific sentence is the only thing carrying which field is wrong, and the
 * catalog can only ever say something generic. Both codes therefore set
 * `preferServerMessage`, exactly as the menu-board publish refusals do.
 */

import {
  trxErrorField,
  trxErrorMessage,
  trxErrorType,
  type TrxPayPageError,
} from './trxPayPageTypes';
import { describeThrown } from './trxFieldsDiagnostics';

/** The card hosted fields refused to tokenize. Nothing was charged. */
export const CARD_FIELDS_REJECTED = 'CARD_FIELDS_REJECTED';

/** The bank-account hosted fields refused to tokenize. Nothing was debited. */
export const BANK_FIELDS_REJECTED = 'BANK_FIELDS_REJECTED';

/** Submit was attempted before the hosted fields finished initializing. */
export const CARD_FIELDS_NOT_READY = 'CARD_FIELDS_NOT_READY';

/** Submit was attempted with the card fields still blank / incomplete. */
export const CARD_FIELDS_INCOMPLETE = 'CARD_FIELDS_INCOMPLETE';

/**
 * The raw TRX errors ride along on the thrown error so a caller can log them.
 * They are diagnostic only — never rendered.
 */
export interface TrxExtractError extends Error {
  readonly code: string;
  readonly trxErrors?: readonly TrxPayPageError[];
}

/**
 * TRX's field ids (the keys of the `fields` map handed to
 * `PayPageJS.initialize`) → the words the operator sees on screen.
 *
 * Naming the field is the whole point: "check your card details" sends someone
 * hunting across three inputs, and an operator reading a card number back to a
 * cardholder on the phone needs to know it is the NUMBER that is wrong.
 */
const CARD_FIELD_LABELS: Readonly<Record<string, string>> = {
  'card-number': 'card number',
  'expiration-date': 'expiration date',
  cvv: 'security code',
};

const BANK_FIELD_LABELS: Readonly<Record<string, string>> = {
  routing: 'routing number',
  'account-number': 'account number',
};

function namedField(
  field: string | undefined,
  labels: Readonly<Record<string, string>>,
): string | undefined {
  if (field === undefined) return undefined;
  return labels[field];
}

/** The sentence for a hosted form that could not be brought up. */
export const PAYMENT_FORM_LOAD_FAILED =
  'Payment form failed to load. Please refresh the page and try again.';

/**
 * The sentence for an exception in OUR code after TRX initialized cleanly —
 * deliberately not the load-failure sentence, which would send support to TRX.
 */
export const PAYMENT_FORM_UNEXPECTED_ERROR =
  'The payment form hit an unexpected error. Please refresh the page and try again.';

/**
 * Append TRX's own words to a sentence: "… (TRX: Error loading iframe for
 * 'routing' field. URL is unavailable)". The four load-failure types share ONE
 * sentence, so without this an operator — and support reading their screenshot
 * — cannot tell a blocked iframe from a missing container or a bad session.
 */
export function withTrxDetail(sentence: string, detail: string): string {
  const text = detail.trim();
  return text ? `${sentence} (TRX: ${text})` : sentence;
}

/**
 * Shared §11.9 mapping for the failure types that mean the same thing on both
 * instruments. Returns `undefined` when the type is instrument-specific and the
 * caller must supply its own wording.
 */
function describeSharedTrxError(first: TrxPayPageError): string | undefined {
  switch (trxErrorType(first)) {
    case 'server-error':
      return 'Payment service temporarily unavailable. Please try again in a moment.';
    case 'iframe-error':
    case 'container-error':
    case 'styles-error':
    case 'wrong-config':
      return withTrxDetail(PAYMENT_FORM_LOAD_FAILED, trxErrorMessage(first));
    default:
      return undefined;
  }
}

/**
 * The sentence for a REJECTED `PayPageJS.initialize` — something threw inside
 * TRX's library. Carries the thrown message, which is the only record of why.
 */
export function describeTrxInitRejection(cause: unknown): string {
  return withTrxDetail(PAYMENT_FORM_LOAD_FAILED, describeThrown(cause).message);
}

/** Customer-facing sentence for a CARD hosted-field failure. */
export function describeTrxCardError(
  errors: readonly TrxPayPageError[] | undefined,
): string {
  if (!errors || errors.length === 0) {
    return 'Card input could not be processed. Please try again.';
  }
  const first = errors[0];
  const shared = describeSharedTrxError(first);
  if (shared !== undefined) return shared;

  switch (trxErrorType(first)) {
    case 'wrong-field': {
      const label = namedField(trxErrorField(first), CARD_FIELD_LABELS);
      if (label !== undefined) {
        return `The ${label} is not valid. Please check it and try again.`;
      }
      // The live bundle usually names the field in its TEXT rather than in a
      // `field` key ("Card number is invalid"), so that beats the catch-all.
      return trxErrorMessage(first) ||
        'Please check your card details and try again.';
    }
    case 'permissions-error':
      return 'This business is not currently configured for card deposits. Please try again later.';
    default:
      return (
        trxErrorMessage(first) || 'Please check your card details and try again.'
      );
  }
}

/**
 * Who is looking at a bank form: the PAYER (a restaurant's customer on a pay
 * link) or STAFF (an owner, an operator, OrderUp ops). It decides who a
 * "bank payments are off" sentence sends them to — the business, or OrderUp.
 */
export type TrxBankFormAudience = 'payer' | 'staff';

/**
 * The payment page has no ACH enabled. TRX enables ACH per payment page; until
 * it does, the hosted bank fields refuse with a permissions error ("Payment
 * with ach is not allowed"). That is a provisioning gap, not a load failure, so
 * it must never read as "Payment form failed to load".
 */
export const ACH_NOT_ENABLED_ON_PAYPAGE_STAFF =
  "Bank payments aren't turned on for this payment page yet. Contact OrderUp support to turn them on.";
export const ACH_NOT_ENABLED_ON_PAYPAGE_PAYER =
  "This business can't take bank payments online yet. Please pay another way, or contact the business.";

const ACH_NOT_ENABLED_TEXT = /not allowed|not enabled|not permitted|permission/i;

/** True when TRX's answer says bank payments are not enabled on this page. */
export function isTrxAchNotEnabled(
  errors: readonly TrxPayPageError[] | undefined,
): boolean {
  return (errors ?? []).some(
    (e) =>
      trxErrorType(e) === 'permissions-error' ||
      ACH_NOT_ENABLED_TEXT.test(trxErrorMessage(e)),
  );
}

function achNotEnabledSentence(audience: TrxBankFormAudience): string {
  return audience === 'staff'
    ? ACH_NOT_ENABLED_ON_PAYPAGE_STAFF
    : ACH_NOT_ENABLED_ON_PAYPAGE_PAYER;
}

/**
 * The sentence for a REJECTED `PayPageJS.initialize` on the BANK form. A thrown
 * "not allowed" is the ACH-off case above; anything else is a load failure.
 */
export function describeTrxBankInitRejection(
  cause: unknown,
  audience: TrxBankFormAudience = 'payer',
): string {
  const message = describeThrown(cause).message;
  if (ACH_NOT_ENABLED_TEXT.test(message)) return achNotEnabledSentence(audience);
  return withTrxDetail(PAYMENT_FORM_LOAD_FAILED, message);
}

/** Customer-facing sentence for a BANK-ACCOUNT hosted-field failure. */
export function describeTrxBankError(
  errors: readonly TrxPayPageError[] | undefined,
  audience: TrxBankFormAudience = 'payer',
): string {
  if (!errors || errors.length === 0) {
    return 'Bank-account input could not be processed. Please try again.';
  }
  // Checked before the shared load-failure mapping: TRX can report the
  // ACH-off page under a config type too, and it must not read as a load
  // failure.
  if (isTrxAchNotEnabled(errors)) return achNotEnabledSentence(audience);
  const first = errors[0];
  const shared = describeSharedTrxError(first);
  if (shared !== undefined) return shared;

  switch (trxErrorType(first)) {
    case 'wrong-field': {
      const label = namedField(trxErrorField(first), BANK_FIELD_LABELS);
      if (label !== undefined) {
        return `The ${label} is not valid. Please check it and try again.`;
      }
      return trxErrorMessage(first) ||
        'Please check your routing and account numbers and try again.';
    }
    default:
      return (
        trxErrorMessage(first) || 'Please check your bank details and try again.'
      );
  }
}

function buildExtractError(
  code: string,
  message: string,
  errors: readonly TrxPayPageError[] | undefined,
): TrxExtractError {
  // `code` is what keeps the sentence alive through `translateError`; without
  // it the message below is discarded and the operator sees the generic
  // fallback. Do not drop it.
  return Object.assign(new Error(message), {
    code,
    ...(errors !== undefined ? { trxErrors: errors } : {}),
  });
}

/**
 * A coded card-entry pre-flight refusal — nothing was sent anywhere.
 *
 * These sentences are already the right words for the operator; the code is
 * only what stops `translateError` from replacing them with "Something went
 * wrong. Please try again."
 */
export function buildCardEntryError(
  code: string,
  message: string,
): TrxExtractError {
  return Object.assign(new Error(message), { code });
}

/** The error the CARD extract path throws when TRX refuses to tokenize. */
export function buildTrxCardExtractError(
  errors: readonly TrxPayPageError[] | undefined,
): TrxExtractError {
  return buildExtractError(
    CARD_FIELDS_REJECTED,
    describeTrxCardError(errors),
    errors,
  );
}

/** The error the BANK extract path throws when TRX refuses to tokenize. */
export function buildTrxBankExtractError(
  errors: readonly TrxPayPageError[] | undefined,
  audience: TrxBankFormAudience = 'payer',
): TrxExtractError {
  return buildExtractError(
    BANK_FIELDS_REJECTED,
    describeTrxBankError(errors, audience),
    errors,
  );
}

/**
 * TRX's bank form encrypted the account but returned no last four. The fields
 * initialize with `includeACHInfo`, so TRX — not the person — supplies the
 * digits shown on every saved account; without them nothing is submitted.
 */
export const BANK_LAST_FOUR_MISSING = 'BANK_LAST_FOUR_MISSING';

/**
 * The account's last four as TRX's library returned them (`includeACHInfo`).
 * Throws a coded error when TRX returned none — the caller re-enables the
 * fields and shows the message.
 */
export function requireTrxAchLastFour(payload: {
  readonly accountNumberLastFour?: string;
}): string {
  const digits = (payload.accountNumberLastFour ?? '').replace(/\D/g, '').slice(-4);
  if (/^\d{4}$/.test(digits)) return digits;
  throw buildCardEntryError(
    BANK_LAST_FOUR_MISSING,
    "The secure bank form didn't confirm the account number. Please re-enter it and try again.",
  );
}
